// src/ui/ForwardModal.jsx — pick where a message goes.
//
// The phone's picker is a full-screen list of names. This is the same list with the
// chat-list row it deserves: real avatar (group collage, Note-to-Self glyph, people
// badge), the live decrypted last message, its time, and its ticks — so you can tell
// two "Mohammed"s apart before you send someone a photo by mistake.
//
// Two things the phone can't do and this can:
//   • pick SEVERAL destinations in one go (the app forwards to exactly one, then closes)
//   • search actually filters the recent list as you type, as well as querying for
//     people you have no chat with yet
//
// A destination from search may have no chat document at all. That's fine and needs no
// special case: writeChatDoc() writes with { merge: true } and fills in participants,
// so the first forward creates the conversation.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Search, X, Forward, Check } from './icons';
import Avatar, { resolveName, groupTitle } from './Avatar';
import MessageTicks, { deriveReceiptStatus } from './MessageTicks';
import { timeLabel } from './format';
import { otherParticipant, isNoteToSelf, isAiChat, clearedAtMs, tsMs } from '../services/chats';
import { isOfficialChat, OFFICIAL_UID } from '../services/support';
import { getUserProfile, searchUsers } from '../services/users';
import { watchChatPreviews } from '../services/previews';
import { forwardToMany, canForward, forwardPreviewOf, ensureDirectChat } from '../services/forward';
import { unblockUser } from '../services/blocking';   // 🚫

const SEARCH_DEBOUNCE = 300;

/** One destination row — chat or a searched person. */
function Row({ dest, myUid, preview, selected, disabled, name, profile, onToggle }) {
  const chat = dest.chat;
  const peer = chat ? otherParticipant(chat, myUid) : dest.user?.uid;

  // the same "cleared chat says nothing" rule the sidebar uses
  const clearedMs = chat ? clearedAtMs(chat, myUid) : 0;
  const previewMs = preview?.ts ? tsMs(preview.ts) : 0;
  const wiped = !!clearedMs && !!previewMs && previewMs <= clearedMs;
  const sub = dest.user
    ? (dest.user.username ? `@${dest.user.username}` : dest.user.phone || dest.user.email || 'Allo user')
    : (wiped ? '' : preview?.text || '');
  const mine = preview?.senderId === myUid;

  return (
    <button
      type="button"
      className={`fw-row ${selected ? 'on' : ''}`}
      disabled={disabled}
      onClick={() => onToggle(dest)}
    >
      <span className="fw-av">
        <Avatar
          profile={chat?.isGroup ? chat : (profile || dest.user)}
          chat={chat || { participants: [myUid, peer].filter(Boolean) }}
          myUid={myUid}
          peerUid={peer}
        />
      </span>

      <span className="fw-mid">
        <span className="fw-top">
          <span className="fw-name">{name}</span>
          {!dest.user && preview?.ts && <span className="fw-time">{timeLabel(preview.ts)}</span>}
        </span>
        <span className="fw-sub">
          {!dest.user && mine && !wiped && preview && (
            <MessageTicks
              status={deriveReceiptStatus(preview, chat, myUid)}
              size={13} color="var(--tick-grey)" readColor="var(--tick-read)"
            />
          )}
          <span className="fw-sub-t">{sub || (dest.user ? '' : 'No messages yet')}</span>
          {dest.user && <span className="fw-new">New chat</span>}
        </span>
      </span>

      <span className={`fw-check ${selected ? 'on' : ''}`}>{selected && <Check width={14} height={14} />}</span>
    </button>
  );
}

export default function ForwardModal({ payload, chats, myUid, myName, onClose, onSent, blocking }) {
  const message = payload?.message || payload;          // tolerate the old bare-message shape
  const originalSenderName = payload?.originalSenderName || 'Unknown User';
  const sourceChatId = payload?.sourceChatId;

  const [q, setQ] = useState('');
  const [found, setFound] = useState([]);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState([]);            // array of dest objects, in tap order
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [previews, setPreviews] = useState({});
  const [profiles, setProfiles] = useState({});        // uid -> profile, shared by rows and search
  const timer = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  // Esc closes, exactly like every other dismissible surface here
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, busy]);

  // 🟢 the AI channel is read-only and hidden from this client entirely, so it must not
  //    be offerable as a forward destination either
  const rooms = useMemo(
    // 🟥 A BLOCKED USER IS NOT A DESTINATION — either direction. Forwarding is a send,
    //    and every other send door is closed; a picker that still lists them would be
    //    the one gap (and the exact bug report this guards against: forward to a
    //    blocked user by scrolling the recents). Groups stay: blocking is 1:1 only,
    //    same as the app.
    () => (chats || []).filter((c) => {
      if (isAiChat(c)) return false;
      /* 💬 NOR IS THE OFFICIAL ALLO ACCOUNT. That thread is a support channel
         with a server-enforced turn: a forwarded message is written straight to
         Firestore, so it would bypass /api/support/submit entirely — landing in
         a queue as an untitled fragment with no ticket, no category and no
         context, and possibly while the composer is supposed to be locked. If
         someone wants to send us something, Help asks them what it is about. */
      if (isOfficialChat(c)) return false;
      /* 🚫 A BLOCKED PERSON STAYS IN THE LIST — with no picture — and is refused
         at the tap, where the blocker is offered to unblock and continue (see
         `toggle`). Hiding the row left them wondering where the chat went. */
      return true;
    }),
    [chats, myUid],
  );
  const roomIds = useMemo(() => rooms.map((c) => c.id).join(','), [rooms]);

  // 🟥 THE FILTER AND THE ROW MUST AGREE ON THE NAME. The first draft filtered on
  //    chat.participantNames — a field the web never writes for the peer — while the row
  //    displayed the name from the users collection. Typing the name you could plainly
  //    see made that row disappear. One shared lookup, one source of truth.
  useEffect(() => {
    let live = true;
    const peers = rooms.filter((c) => !c.isGroup).map((c) => otherParticipant(c, myUid)).filter(Boolean);
    const missing = [...new Set(peers)].filter((u) => !profiles[u]);
    if (!missing.length) return undefined;
    Promise.all(missing.map((u) => getUserProfile(u).then((p) => [u, p]).catch(() => [u, null])))
      .then((pairs) => {
        if (!live) return;
        setProfiles((prev) => {
          const next = { ...prev };
          pairs.forEach(([u, p]) => { if (p) next[u] = p; });
          return next;
        });
      });
    return () => { live = false; };
  }, [rooms, myUid, profiles]);

  useEffect(() => {
    if (!rooms.length) return undefined;
    const un = watchChatPreviews(roomIds.split(','), myUid, (chatId, p) => {
      setPreviews((prev) => ({ ...prev, [chatId]: p }));
    });
    return () => un && un();
    // 🟥 keyed on the ID STRING, not the array. watchChats rebuilds its array on every
    //    snapshot — every receipt, every unread bump — so depending on `rooms` tore down
    //    and re-created one listener per chat continuously, including once per
    //    destination while our own forward was writing chat docs. (Sidebar already
    //    learned this; this copy had dropped it.)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomIds, myUid]);

  // ── search: filter what you have, and look up what you don't ──
  useEffect(() => {
    clearTimeout(timer.current);
    const term = q.trim();
    if (term.length < 2) { setFound([]); setSearching(false); return undefined; }
    setSearching(true);
    timer.current = setTimeout(async () => {
      try {
        const r = await searchUsers(term, myUid);
        setFound(r);
      } catch { setFound([]); } finally { setSearching(false); }
    }, SEARCH_DEBOUNCE);
    return () => clearTimeout(timer.current);
  }, [q, myUid]);

  const nameOfChat = useCallback((c) => {
    if (c.isGroup) return groupTitle(c);
    if (isNoteToSelf(c, myUid)) return 'Note to Self';
    const peer = otherParticipant(c, myUid);
    return resolveName(profiles[peer]) !== 'Allo user'
      ? resolveName(profiles[peer])
      : (c.participantNames?.[peer] || 'Allo user');
  }, [myUid, profiles]);

  const term = q.trim().toLowerCase();
  const visibleRooms = useMemo(() => {
    if (!term) return rooms;
    return rooms.filter((c) => {
      const n = nameOfChat(c).toLowerCase();
      return n.includes(term);
    });
  }, [rooms, term, nameOfChat]);

  // people we already have a chat with must not appear twice
  const knownPeers = useMemo(() => new Set(
    rooms.filter((c) => !c.isGroup).map((c) => otherParticipant(c, myUid)),
  ), [rooms, myUid]);
  const newPeople = useMemo(
    () => found.filter((u) => !knownPeers.has(u.uid)),
    [found, knownPeers],
  );

  const keyOf = (d) => (d.chat ? d.chat.id : `u:${d.user.uid}`);
  const isPicked = (d) => picked.some((p) => keyOf(p) === keyOf(d));
  const _pick = (d) => setPicked((prev) => (
    prev.some((p) => keyOf(p) === keyOf(d)) ? prev.filter((p) => keyOf(p) !== keyOf(d)) : [...prev, d]
  ));
  /* 🚫 A BLOCKED PERSON IS NOT A DESTINATION — either direction. The one who
     blocked is offered the door back; the one who was blocked is told. */
  const toggle = (d) => {
    const peer = d.chat ? (d.chat.isGroup ? null : otherParticipant(d.chat, myUid)) : d.user?.uid;
    if (peer && blocking) {
      const nm = d.user?.fullName || d.user?.username || d.chat?.participantNames?.[peer] || 'this person';
      if (blocking.byMe.has(peer)) {
        if (!window.confirm(`You blocked ${nm}. Unblock and send?`)) return;
        unblockUser(myUid, peer).then(() => _pick(d)).catch(() => window.alert('Could not unblock. Please try again.'));
        return;
      }
      if (blocking.me.has(peer)) { window.alert(`You can’t send messages to ${nm} right now.`); return; }
    }
    _pick(d);
  };

  const send = async () => {
    if (!picked.length || busy) return;
    setBusy(true); setNote('');
    // 🟥 SNAPSHOT THE SELECTION. Rows are frozen while this runs, but `picked` is still
    //    read from this render's closure — rebuilding state from it after the await
    //    would realign results[i] against a list that had moved on.
    const batch = picked;
    const targets = batch.map((d) => (d.chat ? d.chat : {
      id: [myUid, d.user.uid].sort().join('_'),
      participants: [myUid, d.user.uid].sort(),
      isGroup: false,
    }));
    // a destination picked from search may have no chat document yet
    await Promise.all(batch.map((d, i) => (d.user
      ? ensureDirectChat(targets[i].id, targets[i].participants)
      : Promise.resolve())));

    const results = await forwardToMany({
      message, chats: targets, myUid, myName, originalSenderName, sourceChatId,
    });
    const bad = results.filter((r) => !r.ok);
    setBusy(false);
    if (!bad.length) { onSent?.(results.length); onClose(); return; }
    // 🟢 partial failure is REPORTED, not swallowed: some destinations may have no
    //    device keys yet, and silently "succeeding" would be a lie
    setNote(bad.length === results.length
      ? (bad[0].error || 'Could not forward that.')
      : `Sent to ${results.length - bad.length} of ${results.length}. ${bad[0].error || ''}`);
    // leave exactly the destinations that failed selected, so Forward retries them
    setPicked(batch.filter((d, i) => !results[i]?.ok));
  };

  const blocked = !canForward(message);
  const what = message?.type === 'text'
    ? (message._plain || '').slice(0, 60)
    : forwardPreviewOf(message) || 'message';

  return (
    <div className="overlay" onClick={() => !busy && onClose()}>
      <div className="fw" onClick={(e) => e.stopPropagation()}>
        <div className="fw-head">
          <div>
            <div className="fw-title">Forward to…</div>
            <div className="fw-what" title={what}>{what}</div>
          </div>
          <button className="icon-btn" onClick={() => !busy && onClose()} disabled={busy} title="Close"><X /></button>
        </div>

        <div className="fw-search">
          <Search width={17} height={17} />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search chats, or find someone by name, username or phone"
          />
          {q && <button className="fw-clear" onClick={() => setQ('')}><X width={14} height={14} /></button>}
        </div>

        <div className="fw-list">
          {blocked && <div className="fw-empty">This message can’t be forwarded.</div>}

          {!blocked && !!visibleRooms.length && (
            <>
              <div className="fw-sec">{term ? 'Your chats' : 'Recent'}</div>
              {visibleRooms.map((c) => (
                <Row
                  key={c.id}
                  dest={{ chat: c }}
                  myUid={myUid}
                  name={nameOfChat(c)}
                  profile={c.isGroup ? c : profiles[otherParticipant(c, myUid)]}
                  preview={previews[c.id]}
                  selected={isPicked({ chat: c })}
                  disabled={busy}
                  onToggle={toggle}
                />
              ))}
            </>
          )}

          {!blocked && !!newPeople.length && (
            <>
              <div className="fw-sec">Other people</div>
              {newPeople.map((u) => (
                <Row
                  key={u.uid}
                  dest={{ user: u }}
                  myUid={myUid}
                  name={resolveName(u)}
                  profile={u}
                  selected={isPicked({ user: u })}
                  disabled={busy}
                  onToggle={toggle}
                />
              ))}
            </>
          )}

          {!blocked && searching && <div className="fw-empty">Searching…</div>}
          {!blocked && !searching && !visibleRooms.length && !newPeople.length && (
            <div className="fw-empty">
              {term.length < 2 ? 'No conversations yet.' : `Nobody found for “${q.trim()}”.`}
            </div>
          )}
        </div>

        {!!picked.length && (
          <div className="fw-chips">
            {picked.map((d) => (
              <span className="fw-chip" key={keyOf(d)}>
                <span>{d.chat ? nameOfChat(d.chat) : resolveName(d.user)}</span>
                <button onClick={() => !busy && toggle(d)} aria-label="Remove"><X width={12} height={12} /></button>
              </span>
            ))}
          </div>
        )}

        {note && <div className="fw-note">{note}</div>}

        <div className="fw-foot">
          <button className="btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn primary" onClick={send} disabled={!picked.length || busy || blocked}>
            {busy ? 'Forwarding…' : <><Forward width={16} height={16} /> Forward{picked.length > 1 ? ` (${picked.length})` : ''}</>}
          </button>
        </div>
      </div>
    </div>
  );
}
